/**
 * Analyse d'un logo à l'import (étape 1 du plan validé par Daniel le 09.10.2026, revue Neo) : on MESURE et on JOURNALISE,
 * on ne décide rien. Les mesures servent à calibrer la future règle « vectoriser / améliorer / refuser » sur de vrais logos.
 *
 * Tout tourne dans le navigateur sur une copie ≤ 600 px (< 500 ms) :
 *  - fond : alpha s'il y en a, sinon couleur médiane du cadre d'un pixel ;
 *  - « platitude » : part des pixels intérieurs (loin des bords forts) hors des 8 couleurs principales, après médiane 3×3
 *    (le JPEG compressé ne doit pas passer pour un dégradé) ;
 *  - détails : hauteur des composantes « lettre » PAR CLASSE DE COULEUR (texte clair ET sombre), p10 et médiane ;
 *  - traits : largeur sur les crêtes de la distance (par classe de couleur), p10 et médiane.
 * Les tailles sont ramenées en pixels du FICHIER source.
 */

export const LOGO_METRICS_VERSION = 1;

export interface LogoMetrics {
	version: number;
	w: number;
	h: number;
	hasAlpha: boolean;
	bg: { r: number; g: number; b: number } | null;
	fgPct: number;
	colors: number;
	flatPct: number | null;
	letters: { n: number; p10: number | null; med: number | null };
	stroke: { p10: number | null; med: number | null };
	ms: number;
}

const loadImage = (src: string): Promise<HTMLImageElement> =>
	new Promise((resolve, reject) => {
		const im = new Image();
		im.onload = () => resolve(im);
		im.onerror = () => reject(new Error('image load failed'));
		im.src = src;
	});

const med9 = (a: number[]): number => {
	a.sort((x, y) => x - y);
	return a[4];
};

/** chamfer 3-4 : distance (×3 par pixel) de chaque pixel « hors masque » au masque ; dist = 0 sur le masque, INF ailleurs */
const chamfer = (dist: Int32Array, w: number, h: number): void => {
	for (let y = 0; y < h; y++) {
		for (let x = 0; x < w; x++) {
			const i = y * w + x;
			let d = dist[i];
			if (x > 0 && dist[i - 1] + 3 < d) d = dist[i - 1] + 3;
			if (y > 0) {
				if (dist[i - w] + 3 < d) d = dist[i - w] + 3;
				if (x > 0 && dist[i - w - 1] + 4 < d) d = dist[i - w - 1] + 4;
				if (x < w - 1 && dist[i - w + 1] + 4 < d) d = dist[i - w + 1] + 4;
			}
			dist[i] = d;
		}
	}
	for (let y = h - 1; y >= 0; y--) {
		for (let x = w - 1; x >= 0; x--) {
			const i = y * w + x;
			let d = dist[i];
			if (x < w - 1 && dist[i + 1] + 3 < d) d = dist[i + 1] + 3;
			if (y < h - 1) {
				if (dist[i + w] + 3 < d) d = dist[i + w] + 3;
				if (x < w - 1 && dist[i + w + 1] + 4 < d) d = dist[i + w + 1] + 4;
				if (x > 0 && dist[i + w - 1] + 4 < d) d = dist[i + w - 1] + 4;
			}
			dist[i] = d;
		}
	}
};

const pct = (sorted: number[], p: number): number | null => (sorted.length ? sorted[Math.min(sorted.length - 1, Math.floor(p * sorted.length))] : null);

export async function computeLogoMetrics(src: string): Promise<LogoMetrics> {
	const t0 = performance.now();
	const im = await loadImage(src);
	const sw = im.naturalWidth || 1;
	const sh = im.naturalHeight || 1;
	const scale = Math.min(1, 600 / Math.max(sw, sh));
	const w = Math.max(1, Math.round(sw * scale));
	const h = Math.max(1, Math.round(sh * scale));
	const k = sw / w; // facteur vers les pixels du fichier
	const c = document.createElement('canvas');
	c.width = w;
	c.height = h;
	const ctx = c.getContext('2d', { willReadFrequently: true });
	if (!ctx) throw new Error('canvas');
	ctx.drawImage(im, 0, 0, w, h);
	const d = ctx.getImageData(0, 0, w, h).data;
	const n = w * h;

	// --- fond et premier plan
	let hasAlpha = false;
	for (let i = 3; i < d.length; i += 4) if (d[i] < 250) { hasAlpha = true; break; }
	let bg: LogoMetrics['bg'] = null;
	const fg = new Uint8Array(n);
	if (hasAlpha) {
		for (let i = 0; i < n; i++) fg[i] = d[i * 4 + 3] >= 16 ? 1 : 0;
	} else {
		const rs: number[] = [];
		const gs: number[] = [];
		const bs: number[] = [];
		const push = (i: number) => { rs.push(d[i * 4]); gs.push(d[i * 4 + 1]); bs.push(d[i * 4 + 2]); };
		for (let x = 0; x < w; x++) { push(x); push((h - 1) * w + x); }
		for (let y = 0; y < h; y++) { push(y * w); push(y * w + w - 1); }
		const m = (a: number[]) => { a.sort((x, y) => x - y); return a[a.length >> 1]; };
		bg = { r: m(rs), g: m(gs), b: m(bs) };
		for (let i = 0; i < n; i++) {
			const dr = d[i * 4] - bg.r;
			const dg = d[i * 4 + 1] - bg.g;
			const db = d[i * 4 + 2] - bg.b;
			fg[i] = dr * dr + dg * dg + db * db > 40 * 40 ? 1 : 0;
		}
	}
	let fgCount = 0;
	for (let i = 0; i < n; i++) fgCount += fg[i];

	// --- médiane 3×3 (anti-JPEG) + niveaux de gris
	const med = new Uint8ClampedArray(n * 3);
	const gray = new Float32Array(n);
	const buf: number[] = new Array(9);
	for (let y = 0; y < h; y++) {
		for (let x = 0; x < w; x++) {
			const i = y * w + x;
			for (let ch = 0; ch < 3; ch++) {
				let q = 0;
				for (let dy = -1; dy <= 1; dy++) {
					const yy = Math.min(h - 1, Math.max(0, y + dy));
					for (let dx = -1; dx <= 1; dx++) {
						const xx = Math.min(w - 1, Math.max(0, x + dx));
						buf[q++] = d[(yy * w + xx) * 4 + ch];
					}
				}
				med[i * 3 + ch] = med9(buf.slice());
			}
			gray[i] = 0.299 * med[i * 3] + 0.587 * med[i * 3 + 1] + 0.114 * med[i * 3 + 2];
		}
	}

	// --- bords forts (Sobel) et intérieur = premier plan à > 5 px d'un bord fort
	const INF = 0x3fffffff;
	const dist = new Int32Array(n).fill(INF);
	for (let y = 1; y < h - 1; y++) {
		for (let x = 1; x < w - 1; x++) {
			const i = y * w + x;
			const gx = (gray[i - w + 1] + 2 * gray[i + 1] + gray[i + w + 1] - gray[i - w - 1] - 2 * gray[i - 1] - gray[i + w - 1]) / 8;
			const gy = (gray[i + w - 1] + 2 * gray[i + w] + gray[i + w + 1] - gray[i - w - 1] - 2 * gray[i - w] - gray[i - w + 1]) / 8;
			if (gx * gx + gy * gy > 100) dist[i] = 0;
		}
	}
	chamfer(dist, w, h);
	const interior = new Uint8Array(n);
	let intCount = 0;
	for (let i = 0; i < n; i++) if (fg[i] && dist[i] > 15) { interior[i] = 1; intCount++; }

	// --- couleurs intérieures (bins de 8 niveaux) : nombre de couleurs significatives, part hors des 8 principales
	let colors = 0;
	let flatPct: number | null = null;
	const hist = new Map<number, number>();
	if (intCount >= 200) {
		for (let i = 0; i < n; i++) {
			if (!interior[i]) continue;
			const key = ((med[i * 3] >> 3) << 10) | ((med[i * 3 + 1] >> 3) << 5) | (med[i * 3 + 2] >> 3);
			hist.set(key, (hist.get(key) || 0) + 1);
		}
		const counts = Array.from(hist.values()).sort((a, b) => b - a);
		colors = counts.filter(v => v >= 0.003 * intCount).length;
		let rest = 0;
		for (let i = 8; i < counts.length; i++) rest += counts[i];
		flatPct = Math.round((rest / intCount) * 1000) / 10;
	}

	// --- classes de couleur du premier plan (jusqu'à 6, par centres = bins les plus fréquents du premier plan entier)
	const histFg = new Map<number, number>();
	for (let i = 0; i < n; i++) {
		if (!fg[i]) continue;
		const key = ((med[i * 3] >> 4) << 8) | ((med[i * 3 + 1] >> 4) << 4) | (med[i * 3 + 2] >> 4);
		histFg.set(key, (histFg.get(key) || 0) + 1);
	}
	const centers = Array.from(histFg.entries()).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([key]) => ({
		r: ((key >> 8) & 15) * 16 + 8, g: ((key >> 4) & 15) * 16 + 8, b: (key & 15) * 16 + 8,
	}));
	const cls = new Int8Array(n).fill(-1);
	for (let i = 0; i < n; i++) {
		if (!fg[i]) continue;
		let best = -1;
		let bd = Infinity;
		for (let cI = 0; cI < centers.length; cI++) {
			const cc = centers[cI];
			const dd = (med[i * 3] - cc.r) ** 2 + (med[i * 3 + 1] - cc.g) ** 2 + (med[i * 3 + 2] - cc.b) ** 2;
			if (dd < bd) { bd = dd; best = cI; }
		}
		cls[i] = best;
	}

	// --- par classe : composantes « lettre » (hauteur) et largeur des traits (crêtes de la distance)
	const heights: number[] = [];
	const widths: number[] = [];
	const seen = new Uint8Array(n);
	const stack = new Int32Array(n);
	const maxDim = 0.25 * Math.max(w, h);
	for (let cI = 0; cI < centers.length; cI++) {
		const mask = new Uint8Array(n);
		let cnt = 0;
		for (let i = 0; i < n; i++) if (cls[i] === cI) { mask[i] = 1; cnt++; }
		if (cnt < 12) continue;
		// composantes 8-connexes
		seen.fill(0);
		for (let s = 0; s < n; s++) {
			if (!mask[s] || seen[s]) continue;
			let sp = 0;
			stack[sp++] = s;
			seen[s] = 1;
			let area = 0;
			let minX = w; let maxX = -1; let minY = h; let maxY = -1;
			while (sp) {
				const p = stack[--sp];
				area++;
				const x = p % w;
				const y = (p - x) / w;
				if (x < minX) minX = x; if (x > maxX) maxX = x; if (y < minY) minY = y; if (y > maxY) maxY = y;
				for (let dy = -1; dy <= 1; dy++) {
					const yy = y + dy;
					if (yy < 0 || yy >= h) continue;
					for (let dx = -1; dx <= 1; dx++) {
						const xx = x + dx;
						if ((dx | dy) === 0 || xx < 0 || xx >= w) continue;
						const q = yy * w + xx;
						if (mask[q] && !seen[q]) { seen[q] = 1; stack[sp++] = q; }
					}
				}
			}
			const bw = maxX - minX + 1;
			const bh = maxY - minY + 1;
			const ratio = bw / bh;
			if (area >= 12 && ratio >= 0.25 && ratio <= 4 && Math.max(bw, bh) <= maxDim) heights.push(Math.max(bw, bh) * k);
		}
		// largeur des traits : distance au complément, crêtes = maxima locaux
		const dt = new Int32Array(n);
		for (let i = 0; i < n; i++) dt[i] = mask[i] ? INF : 0;
		chamfer(dt, w, h);
		for (let y = 1; y < h - 1; y++) {
			for (let x = 1; x < w - 1; x++) {
				const i = y * w + x;
				const v = dt[i];
				if (v < 3) continue;
				if (v >= dt[i - 1] && v >= dt[i + 1] && v >= dt[i - w] && v >= dt[i + w] && v >= dt[i - w - 1] && v >= dt[i - w + 1] && v >= dt[i + w - 1] && v >= dt[i + w + 1]) {
					widths.push(((2 * v) / 3) * k);
				}
			}
		}
	}
	heights.sort((a, b) => a - b);
	widths.sort((a, b) => a - b);
	const r1 = (v: number | null) => (v === null ? null : Math.round(v * 10) / 10);
	return {
		version: LOGO_METRICS_VERSION,
		w: sw,
		h: sh,
		hasAlpha,
		bg,
		fgPct: Math.round((fgCount / n) * 1000) / 10,
		colors,
		flatPct,
		letters: { n: heights.length, p10: r1(pct(heights, 0.1)), med: r1(pct(heights, 0.5)) },
		stroke: { p10: r1(pct(widths, 0.1)), med: r1(pct(widths, 0.5)) },
		ms: Math.round(performance.now() - t0),
	};
}

/** Journalise (sans bloquer, sans jamais échouer côté client). Aucune donnée personnelle : pas de nom de fichier, pas d'image. */
export function logLogoMetrics(m: LogoMetrics, extra: { kind: string; widthCm: number }): void {
	try {
		const body = JSON.stringify({ ...m, kind: extra.kind, widthCm: extra.widthCm, env: window.location.hostname });
		if (body.length > 4000) return;
		fetch('/api/logo-metrics', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => undefined);
	} catch {
		// silencieux : la journalisation ne doit jamais gêner l'import
	}
}
